// TRACK C — corrected LCOE for the REPORT LCOE surfaces.
//
// SINGLE SOURCE OF TRUTH for the levelized-cost figures shown in the report
// renderers (the BTM overview table in SiteRagReportView and the LcoeChartSection
// bar chart). Both surfaces previously baked in HARDCODED literature bands
// (nuclear 65–100, gas 45–75, solar 40–80, wind 35–90 $/MWh) plus a crude
// land-area heuristic. Those bands are NOT the project's actual LCOE: they
// ignore the load→nameplate overbuild that firming a flat 24/7 datacenter load
// on a low-capacity-factor source requires (a solar+BESS build sized to SERVE
// the load is materially more expensive than its bare generation band), and
// they ignore the project's WACC / phasing / COD.
//
// This module resolves the AUTHORITATIVE per-technology LCOE for a report:
//
//   1. PERSISTED — `report.pathwayLcoeAtGen` (stamped by Track B at generation
//      time). Preferred for reproducibility: a saved report shows the figure
//      that was frozen in, not a recomputation against possibly-drifted libs.
//   2. COMPUTED LIVE — from the gen-time project intent. We build intent-FAITHFUL
//      pathways (`pathwaysFromIntentPhases`), which already gross the facility
//      load up to the required generation NAMEPLATE via
//      `resolveGenerationNameplateForTech` (the load→nameplate source of truth),
//      then take each pathway's WACC-discounted real LCOE from
//      `computePathwayLcoe`. No band is fabricated.
//   3. PENDING — when neither a persisted figure nor an intent is available, the
//      surface degrades to `null` (rendered as "pending"), NOT a fabricated band.
//
// UNITS: USD per MWh throughout. Pure financial arithmetic — no CoolProp /
// IAPWS boundary here. The corrected LCOE comes straight from the validated
// finDecision cashflow engine; this module only ROUTES + GROUPS it.

import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteContext } from '../../types/siteContext';
import { emptySiteContext } from '../../types/siteContext';
import type {
  FinancialAssumptions,
  FinancialAssumptionsCore,
  FinancialTechnology,
  ScenarioAdjustments,
} from '../../types/financialAssumptions';
import type { LcoeBasis } from '../lcoeBasis';
import {
  computeIncentiveAdjustedLcoe,
  convertCoreToUsd,
  type DisaggregatedIncentiveCredits,
  type IncentiveApplicationStatus,
  type IncentiveIneligibilityReason,
} from '../projectFinance';
import type { Pathway, TechnologyId } from './types';
import { pathwaysFromIntentPhases } from './intentPosture';
import { computePathwayLcoe } from './pathwayLcoe';

/**
 * Coarse technology BUCKET used to match the report's free-text BTM technology
 * labels (e.g. "Nuclear SMR", "Natural Gas", "Solar + BESS") and the fixed
 * chart rows. The finance presets are finer-grained (`gas-cc` vs `gas-sc`,
 * `nuclear-smr` vs `nuclear-large`); we collapse to the bucket the surfaces
 * actually render so a single corrected figure backs each visible row.
 */
export type LcoeTechBucket = 'nuclear' | 'gas' | 'fuel-cell' | 'solar' | 'wind' | 'grid' | 'mixed';

/** Where a corrected LCOE figure came from — drives "pending" vs a real number. */
export type LcoeSurfaceSource = 'persisted' | 'computed' | 'pending';

/** One corrected LCOE entry for a technology bucket. */
export interface CorrectedLcoeEntry {
  /** WACC-discounted LCOE midpoint/representative value, USD/MWh. `null` ⇒ pending (no source). */
  midUsdPerMwh: number | null;
  /** Low end of actual evidenced scenarios/pathways, USD/MWh (`=== midUsdPerMwh` when 1 scenario). */
  lowUsdPerMwh?: number | null;
  /** High end of actual evidenced scenarios/pathways, USD/MWh (`=== midUsdPerMwh` when 1 scenario). */
  highUsdPerMwh?: number | null;
  /** Number of evidenced scenarios/pathways backing this bucket. */
  scenarioCount?: number;
  source: LcoeSurfaceSource;
  /**
   * `'project-finance'` when resolved from the user's saved financial model
   * (`financialAssumptionsAtGen` / `financialAssumptions`), or `'benchmark-pathway'`
   * when resolved from `pathwayLcoeAtGen` / `pathwaysFromIntentPhases`.
   */
  basis: LcoeBasis;
  /** Reference currency year for the LCOE (e.g. 2024). */
  currencyYear?: number | string | null;
  /** Whether the figure is constant (real) or nominal dollars. */
  currencyBasis?: 'real' | 'nominal';
  /** Historical/engine labels retained as provenance, not an authenticated dollar basis. */
  reportedCurrencyBases?: Array<'real' | 'nominal'>;
  /** Whether tax credit scenario assumptions (ITC/PTC) are included. */
  subsidyStatus?: 'unsubsidized' | 'subsidized';
  /** Explicit status of incentive scenario assumptions (none, withheld, or user-assumed-scenario). */
  incentiveStatus?: IncentiveApplicationStatus;
  /** Reason incentives were withheld when `subsidyStatus === 'unsubsidized'` on a model with entered credit rates. */
  ineligibilityReason?: IncentiveIneligibilityReason;
  /** Disaggregated credit basis, election, monetization, timing, and PV breakdown when applied. */
  disaggregatedCredits?: DisaggregatedIncentiveCredits;
  /** Human-readable summary of applied scenario incentive assumptions. */
  subsidySummary?: string;
  /** Firming / sizing scope ("24/7-load-sized" vs "plant-level-model"). */
  firmingBasis?: '24/7-load-sized' | 'plant-level-model';
  /** Service boundary ("served-load" vs "busbar"). */
  serviceScope?: 'served-load' | 'busbar';
  /** Deployment maturity scenario when applicable (e.g. SMR FOAK vs NOAK). */
  maturityScenario?: 'FOAK' | 'NOAK' | 'FOAK-and-NOAK' | 'unspecified';
}

export interface CorrectedLcoeSurface {
  /** Per-bucket corrected LCOE. Buckets absent from the intent map to pending. */
  byBucket: Record<LcoeTechBucket, CorrectedLcoeEntry>;
  /** Provenance of the whole surface (worst-case: pending if nothing resolved). */
  source: LcoeSurfaceSource;
  /** True when at least one bucket carries a real (non-null) corrected figure. */
  hasAnyCorrected: boolean;
}

/** Map a finance `TechnologyId` to the coarse render bucket. */
export function technologyIdToBucket(tech: TechnologyId): LcoeTechBucket {
  switch (tech) {
    case 'nuclear-smr':
    case 'nuclear-large':
      return 'nuclear';
    case 'gas-cc':
    case 'gas-sc':
      return 'gas';
    case 'fuel-cell-sofc':
    case 'fuel-cell-pafc':
    case 'fuel-cell-pem':
    case 'fuel-cell-mcfc':
      return 'fuel-cell';
    case 'solar-bess':
      return 'solar';
    case 'wind-bess':
      return 'wind';
    case 'grid-tariff':
      return 'grid';
    case 'mixed':
      return 'mixed';
    default: {
      // Exhaustiveness guard — a new TechnologyId must be bucketed explicitly.
      const _never: never = tech;
      void _never;
      return 'mixed';
    }
  }
}

/**
 * Match a report's free-text BTM technology label (e.g. "Nuclear SMR",
 * "Natural Gas Reciprocating", "Solar + BESS", "Onshore Wind") to a render
 * bucket. Returns `null` when no bucket is recognised (the surface then shows
 * the existing authored value / dash rather than a wrong corrected figure).
 */
export function technologyLabelToBucket(label: string | undefined | null): LcoeTechBucket | null {
  if (!label) return null;
  const t = label.toLowerCase();
  if (t.includes('nuclear') || t.includes('smr')) return 'nuclear';
  // Check fuel-cell keywords BEFORE 'gas' so "SOFC (Natural Gas)" or
  // "Stationary Fuel Cell — Pipeline Gas" never collapses into the combustion
  // 'gas' bucket.
  if (
    t.includes('fuel cell') ||
    t.includes('fuel-cell') ||
    t.includes('sofc') ||
    t.includes('pafc') ||
    t.includes('pemfc') ||
    t.includes('mcfc') ||
    /\bpem\b/.test(t)
  ) {
    return 'fuel-cell';
  }
  if (t.includes('gas')) return 'gas';
  if (t.includes('solar')) return 'solar';
  if (t.includes('wind')) return 'wind';
  if (t.includes('grid')) return 'grid';
  return null;
}

/** A pending entry — no source resolved. */
function pendingEntry(): CorrectedLcoeEntry {
  return { midUsdPerMwh: null, source: 'pending', basis: 'benchmark-pathway' };
}

function emptyByBucket(): Record<LcoeTechBucket, CorrectedLcoeEntry> {
  return {
    nuclear: pendingEntry(),
    gas: pendingEntry(),
    'fuel-cell': pendingEntry(),
    solar: pendingEntry(),
    wind: pendingEntry(),
    grid: pendingEntry(),
    mixed: pendingEntry(),
  };
}

/**
 * Loose shape of `report.pathwayLcoeAtGen` (stamped by Track B). The field is
 * not yet on the `SiteRagReport` type, so we read it defensively: any of a
 * `TechnologyId`-keyed or bucket-keyed record of `{ lcoeRealPerMwhUsd }` /
 * `{ midUsdPerMwh }` / a bare number is accepted. Forward-compatible with Track
 * B's chosen shape without coupling this module to it.
 */
type PersistedLcoeValue =
  | number
  | { lcoeRealPerMwhUsd?: number; lcoeNominalPerMwhUsd?: number; midUsdPerMwh?: number }
  | null
  | undefined;

/** Minimum plausible LCOE in USD/MWh — guards against $/kWh unit-scale errors (e.g. 0.065 $/kWh). */
const MIN_PLAUSIBLE_LCOE_USD_PER_MWH = 1;

function isValidPerMwhUsd(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v >= MIN_PLAUSIBLE_LCOE_USD_PER_MWH;
}

function coercePersistedValueWithBasis(
  v: PersistedLcoeValue,
): { mid: number; currencyBasis?: 'real' | 'nominal' } | null {
  if (isValidPerMwhUsd(v)) return { mid: v, currencyBasis: undefined };
  if (v && typeof v === 'object') {
    if (isValidPerMwhUsd(v.lcoeRealPerMwhUsd)) {
      return { mid: v.lcoeRealPerMwhUsd, currencyBasis: 'real' };
    }
    if (isValidPerMwhUsd(v.midUsdPerMwh)) {
      return { mid: v.midUsdPerMwh, currencyBasis: undefined };
    }
    if (isValidPerMwhUsd(v.lcoeNominalPerMwhUsd)) {
      return { mid: v.lcoeNominalPerMwhUsd, currencyBasis: 'nominal' };
    }
  }
  return null;
}

/**
 * One persisted pathway-LCOE row as written by Track B's
 * `buildPathwayLcoeAtGenSnapshot` (the AUTHORITATIVE persisted shape). The
 * surface is stamped as an ARRAY of these — bucketed here by the `technology`
 * field, NOT by the array index.
 */
interface PersistedLcoeArrayRow {
  pathwayId?: string;
  pathwayName?: string;
  technology?: string;
  lcoeRealPerMwhUsd?: number;
  lcoeNominalPerMwhUsd?: number;
  midUsdPerMwh?: number;
  currencyYear?: number;
  currencyBasis?: 'real' | 'nominal';
  subsidyStatus?: 'unsubsidized' | 'subsidized';
  firmingBasis?: '24/7-load-sized' | 'plant-level-model';
  serviceScope?: 'served-load' | 'busbar';
  maturityScenario?: 'FOAK' | 'NOAK' | 'FOAK-and-NOAK' | 'unspecified';
}

function inferRowMaturity(row?: PersistedLcoeArrayRow | null): 'FOAK' | 'NOAK' | undefined {
  if (!row || typeof row !== 'object') return undefined;
  if (row.maturityScenario === 'FOAK' || row.maturityScenario === 'NOAK') {
    return row.maturityScenario;
  }
  const text = `${row.pathwayId ?? ''} ${row.pathwayName ?? ''}`.toUpperCase();
  if (text.includes('FOAK') && !text.includes('NOAK')) return 'FOAK';
  if (text.includes('NOAK') && !text.includes('FOAK')) return 'NOAK';
  return undefined;
}

function mergeMaturityScenario(
  prev: CorrectedLcoeEntry['maturityScenario'],
  next: 'FOAK' | 'NOAK' | undefined,
): CorrectedLcoeEntry['maturityScenario'] {
  if (!next) return prev;
  if (!prev || prev === 'unspecified') return next;
  if (prev === 'FOAK-and-NOAK' || prev === next) return prev;
  return 'FOAK-and-NOAK';
}

/**
 * Fold one (bucket, midpoint) pair into the accumulating surface, keeping the
 * lowest (most representative) figure when several techs collapse to one
 * bucket — matches the live-compute aggregation — and tracking the actual
 * evidenced scenario range (`lowUsdPerMwh` to `highUsdPerMwh`). Returns true
 * when a real figure was recorded.
 */
function recordBucket(
  byBucket: Record<LcoeTechBucket, CorrectedLcoeEntry>,
  bucket: LcoeTechBucket | null,
  coerced: { mid: number; currencyBasis?: 'real' | 'nominal' } | null,
  maturity?: 'FOAK' | 'NOAK',
  rowBasis?: Pick<
    PersistedLcoeArrayRow,
    'currencyYear' | 'currencyBasis' | 'subsidyStatus' | 'firmingBasis' | 'serviceScope'
  >,
): boolean {
  if (!bucket || !coerced) return false;
  const { mid } = coerced;
  const currencyBasis = rowBasis?.currencyBasis ?? coerced.currencyBasis;
  const currencyYear = rowBasis?.currencyYear;
  const subsidyStatus = rowBasis?.subsidyStatus;
  const firmingBasis = rowBasis?.firmingBasis;
  const serviceScope = rowBasis?.serviceScope;
  const prev = byBucket[bucket];
  const existingMid = prev.midUsdPerMwh;
  const existingLow = prev.lowUsdPerMwh ?? existingMid;
  const existingHigh = prev.highUsdPerMwh ?? existingMid;
  const existingCount = prev.scenarioCount ?? (existingMid != null ? 1 : 0);

  byBucket[bucket] = {
    midUsdPerMwh: existingMid == null ? mid : Math.min(existingMid, mid),
    lowUsdPerMwh: existingLow == null ? mid : Math.min(existingLow, mid),
    highUsdPerMwh: existingHigh == null ? mid : Math.max(existingHigh, mid),
    scenarioCount: existingCount + 1,
    source: 'persisted',
    basis: 'benchmark-pathway',
    currencyYear:
      existingMid == null
        ? currencyYear
        : prev.currencyYear === currencyYear
          ? currencyYear
          : undefined,
    // Field names and legacy labels do not authenticate the pathway engine's
    // cash-flow dollar basis. Keep them as reported provenance only.
    currencyBasis: undefined,
    reportedCurrencyBases: [...new Set([
      ...(prev.reportedCurrencyBases ?? []),
      ...(currencyBasis ? [currencyBasis] : []),
    ])],
    subsidyStatus:
      existingMid == null
        ? subsidyStatus
        : prev.subsidyStatus === subsidyStatus
          ? subsidyStatus
          : undefined,
    firmingBasis:
      existingMid == null
        ? firmingBasis
        : prev.firmingBasis === firmingBasis
          ? firmingBasis
          : undefined,
    serviceScope:
      existingMid == null
        ? serviceScope
        : prev.serviceScope === serviceScope
          ? serviceScope
          : undefined,
    maturityScenario: mergeMaturityScenario(prev.maturityScenario, maturity),
  };
  return true;
}

/**
 * Read a persisted `pathwayLcoeAtGen` surface off the report, if Track B stamped
 * one. Accepts BOTH stamped shapes:
 *   - the AUTHORITATIVE Track-B ARRAY (`buildPathwayLcoeAtGenSnapshot` →
 *     `Array<{ technology, lcoeRealPerMwhUsd, … }>`), bucketed by each row's
 *     `technology` field; and
 *   - a `TechnologyId`-keyed or bucket-keyed RECORD (legacy / forward-compat).
 * Returns null when absent / unusable so the caller falls through to the live
 * compute. Reading an array via `Object.entries` would bucket by the numeric
 * index (never a valid tech/bucket key) and silently drop every row — so the
 * array shape MUST be handled explicitly.
 */
function readPersistedSurface(
  report: SiteRagReport,
): Record<LcoeTechBucket, CorrectedLcoeEntry> | null {
  // The field is read loosely so this module stays decoupled from Track B's
  // exact shape (array OR record).
  const raw = (report as unknown as {
    pathwayLcoeAtGen?: PersistedLcoeArrayRow[] | Record<string, PersistedLcoeValue>;
  }).pathwayLcoeAtGen;
  if (!raw || typeof raw !== 'object') return null;

  const byBucket = emptyByBucket();
  let any = false;

  if (Array.isArray(raw)) {
    // AUTHORITATIVE Track-B shape — bucket by the row's `technology`, not index.
    for (const row of raw) {
      if (!row || typeof row !== 'object') continue;
      const coerced = coercePersistedValueWithBasis(row);
      const bucket = typeof row.technology === 'string'
        ? normaliseKeyToBucket(row.technology)
        : null;
      if (recordBucket(byBucket, bucket, coerced, inferRowMaturity(row), row)) any = true;
    }
    return any ? byBucket : null;
  }

  // RECORD shape — keys may be TechnologyIds or buckets.
  for (const [key, value] of Object.entries(raw)) {
    const coerced = coercePersistedValueWithBasis(value);
    const bucket = normaliseKeyToBucket(key);
    if (recordBucket(byBucket, bucket, coerced)) any = true;
  }
  return any ? byBucket : null;
}

const TECHNOLOGY_IDS: ReadonlySet<string> = new Set([
  'gas-cc',
  'gas-sc',
  'fuel-cell-sofc',
  'fuel-cell-pafc',
  'fuel-cell-pem',
  'fuel-cell-mcfc',
  'nuclear-smr',
  'nuclear-large',
  'solar-bess',
  'wind-bess',
  'grid-tariff',
  'mixed',
]);

const BUCKETS: ReadonlySet<string> = new Set([
  'nuclear',
  'gas',
  'fuel-cell',
  'solar',
  'wind',
  'grid',
  'mixed',
]);

function normaliseKeyToBucket(key: string): LcoeTechBucket | null {
  if (BUCKETS.has(key)) return key as LcoeTechBucket;
  if (TECHNOLOGY_IDS.has(key)) return technologyIdToBucket(key as TechnologyId);
  // Fall back to a label match (defensive — Track B may key by display label).
  return technologyLabelToBucket(key);
}

/**
 * Resolve the project intent to compute against: the gen-time snapshot
 * (`projectIntentAtGen`, the reproducibility invariant) is preferred; a loose
 * `projectIntent` field (used by some renderers) is the fallback.
 */
function resolveIntentForReport(report: SiteRagReport): ProjectIntent | undefined {
  if (report.projectIntentAtGen) return report.projectIntentAtGen;
  const loose = (report as unknown as { projectIntent?: ProjectIntent }).projectIntent;
  return loose ?? undefined;
}

/**
 * Compute the corrected per-bucket LCOE live from the report's intent. Builds
 * intent-faithful pathways (already load→nameplate sized) and takes each
 * pathway's WACC-discounted real LCOE, grouping to the render bucket and
 * keeping the lowest (most representative) figure per bucket. Returns null when
 * there is no usable intent / no resolvable pathway LCOE.
 */
function computeLiveSurface(
  report: SiteRagReport,
  now: Date,
): Record<LcoeTechBucket, CorrectedLcoeEntry> | null {
  const intent = resolveIntentForReport(report);
  if (!intent) return null;

  const siteContext: SiteContext =
    report.siteContextAtGen ??
    (report as unknown as { siteContext?: SiteContext }).siteContext ??
    emptySiteContext();

  let pathways: Pathway[];
  try {
    pathways = pathwaysFromIntentPhases(intent, siteContext, now);
  } catch {
    return null;
  }
  if (pathways.length === 0) return null;

  const byBucket = emptyByBucket();
  let any = false;
  for (const pathway of pathways) {
    if (pathway.economicQualificationPosture === 'unsupported-incomplete') {
      continue;
    }
    const { lcoeRealPerMwhUsd } = computePathwayLcoe(pathway, { now });
    if (!isValidPerMwhUsd(lcoeRealPerMwhUsd)) continue;
    const bucket = technologyIdToBucket(pathway.technology);
    const prev = byBucket[bucket];
    const existingMid = prev.midUsdPerMwh;
    const existingLow = prev.lowUsdPerMwh ?? existingMid;
    const existingHigh = prev.highUsdPerMwh ?? existingMid;
    const existingCount = prev.scenarioCount ?? (existingMid != null ? 1 : 0);
    byBucket[bucket] = {
      midUsdPerMwh: existingMid == null ? lcoeRealPerMwhUsd : Math.min(existingMid, lcoeRealPerMwhUsd),
      lowUsdPerMwh: existingLow == null ? lcoeRealPerMwhUsd : Math.min(existingLow, lcoeRealPerMwhUsd),
      highUsdPerMwh: existingHigh == null ? lcoeRealPerMwhUsd : Math.max(existingHigh, lcoeRealPerMwhUsd),
      scenarioCount: existingCount + 1,
      source: 'computed',
      basis: 'benchmark-pathway',
      // A calculation date does not establish a currency year for mixed benchmark inputs.
      currencyBasis: undefined,
      reportedCurrencyBases: ['real'],
      subsidyStatus: 'unsubsidized',
      firmingBasis: '24/7-load-sized',
      serviceScope: 'served-load',
    };
    any = true;
  }
  return any ? byBucket : null;
}

function financialTechToBucket(tech: FinancialTechnology | undefined | null): LcoeTechBucket | null {
  switch (tech) {
    case 'smr':
      return 'nuclear';
    case 'gas-cc':
    case 'gas-sc':
      return 'gas';
    case 'solar-bess':
      return 'solar';
    case 'wind-bess':
      return 'wind';
    case 'mixed':
      return 'mixed';
    default:
      return null;
  }
}

function applyScenarioAdjustments(
  base: FinancialAssumptionsCore,
  overrides: Partial<FinancialAssumptionsCore>,
  adjustments?: ScenarioAdjustments,
): FinancialAssumptionsCore {
  const merged: FinancialAssumptionsCore = { ...base, ...overrides };
  if (
    overrides.capexTotalUsd == null
    && (overrides.capexPerKwUsd != null || overrides.capacityMW != null)
    && merged.capexPerKwUsd != null
    && merged.capacityMW != null
    && merged.capacityMW > 0
  ) {
    merged.capexTotalUsd = merged.capexPerKwUsd * merged.capacityMW * 1000;
  }
  if (!adjustments) return merged;
  const next = { ...merged } as Record<string, unknown>;
  for (const [field, adj] of Object.entries(adjustments)) {
    if (!adj) continue;
    const cur = next[field];
    if (adj.kind === 'absolute') {
      next[field] = adj.value;
    } else if (typeof cur === 'number' && Number.isFinite(cur)) {
      next[field] = adj.kind === 'multiplier' ? cur * adj.value : cur + adj.value;
    }
  }
  return next as unknown as FinancialAssumptionsCore;
}

function inferMaturityScenario(
  core: FinancialAssumptionsCore,
  fa: FinancialAssumptions,
): 'FOAK' | 'NOAK' | 'unspecified' {
  if (core.technology !== 'smr') return 'unspecified';
  const capexPerKw =
    core.capexPerKwUsd ??
    (core.capexTotalUsd != null && core.capacityMW != null && core.capacityMW > 0
      ? core.capexTotalUsd / (core.capacityMW * 1000)
      : null);
  const coreBaselineId = String(
    (core as unknown as { baselineId?: string }).baselineId ?? '',
  ).toUpperCase();
  // If scenario overrode capexPerKwUsd into the NOAK/FOAK band without changing baselineId, respect the scenario's CAPEX band.
  if (capexPerKw != null && Number.isFinite(capexPerKw) && core.capexPerKwUsd !== fa.capexPerKwUsd) {
    if (capexPerKw >= 5500) return 'FOAK';
    if (capexPerKw > 0 && capexPerKw <= 5000) return 'NOAK';
  }
  if (coreBaselineId.includes('FOAK') && !coreBaselineId.includes('NOAK')) return 'FOAK';
  if (coreBaselineId.includes('NOAK') && !coreBaselineId.includes('FOAK')) return 'NOAK';
  const baselineRefs = Object.values(fa.fieldProvenance ?? {})
    .map((p) => p?.baselineRef ?? '')
    .join(' ')
    .toUpperCase();
  if (baselineRefs.includes('FOAK') && !baselineRefs.includes('NOAK')) return 'FOAK';
  if (baselineRefs.includes('NOAK') && !baselineRefs.includes('FOAK')) return 'NOAK';
  if (capexPerKw != null && Number.isFinite(capexPerKw)) {
    if (capexPerKw >= 5500) return 'FOAK';
    if (capexPerKw > 0 && capexPerKw <= 5000) return 'NOAK';
  }
  return 'unspecified';
}

/**
 * Evaluate a report's saved user financial model (`financialAssumptionsOverride`,
 * `financialAssumptionsAtGen`, or `financialAssumptions`), preserving user
 * inputs, currency conversion (`exchangeRateToUsd`), IRA incentives (`itcPct`,
 * `ptcPerMwhUsd`), FOAK/NOAK distinction, and multi-scenario ranges.
 */
function readSavedFinancialModelEntries(
  report: SiteRagReport,
): Partial<Record<LcoeTechBucket, CorrectedLcoeEntry>> {
  const fa =
    report.financialAssumptionsOverride ??
    report.financialAssumptionsAtGen ??
    (report as unknown as { financialAssumptions?: FinancialAssumptions }).financialAssumptions;
  if (!fa || typeof fa !== 'object') return {};
  if ('businessModelKind' in fa && fa.businessModelKind === 'data-center-capacity-fee') return {};

  const rawFxRate =
    fa.exchangeRateToUsd ?? (fa as unknown as { fxRateToUsd?: number | null }).fxRateToUsd;
  const exchangeRate =
    fa.currency && fa.currency !== 'USD' ? rawFxRate : 1;
  if (
    fa.currency &&
    fa.currency !== 'USD' &&
    (exchangeRate == null || !Number.isFinite(exchangeRate) || exchangeRate <= 0)
  ) {
    // Missing exchange rate for non-USD model — cannot convert safely to USD/MWh.
    return {};
  }

  const candidateCores: Array<{ core: FinancialAssumptionsCore; isCanonical: boolean }> = [
    { core: fa, isCanonical: !(fa.scenarios ?? []).some((s) => s.isCanonical) },
  ];
  for (const sc of fa.scenarios ?? []) {
    if (!sc || typeof sc !== 'object') continue;
    const scOverrides =
      sc.assumptions ??
      (sc as unknown as { overrides?: Partial<FinancialAssumptionsCore> }).overrides ??
      {};
    candidateCores.push({
      core: applyScenarioAdjustments(fa, scOverrides, sc.adjustments),
      isCanonical: Boolean(sc.isCanonical),
    });
  }

  const bucketed = new Map<
    LcoeTechBucket,
    {
      values: number[];
      canonicalVal: number | null;
      currencyBasis: 'real' | 'nominal' | undefined;
      subsidyStatus: 'unsubsidized' | 'subsidized' | undefined;
      incentiveStatus?: IncentiveApplicationStatus;
      ineligibilityReason?: IncentiveIneligibilityReason;
      disaggregatedCredits?: DisaggregatedIncentiveCredits;
      subsidySummary?: string;
      maturities: Set<'FOAK' | 'NOAK'>;
    }
  >();

  for (const { core, isCanonical } of candidateCores) {
    const bucket = financialTechToBucket(core.technology ?? fa.technology);
    if (!bucket) continue;

    const calc = computeIncentiveAdjustedLcoe({
      ...core,
      currency: fa.currency,
      exchangeRateToUsd: exchangeRate,
      incentiveEvidence: core.incentiveEvidence ?? fa.incentiveEvidence,
    });
    const usdCore = convertCoreToUsd(core, exchangeRate);

    let resolvedVal: number | null = null;
    let currencyBasis: 'real' | 'nominal' = 'real';

    if (isValidPerMwhUsd(calc.postIncentiveRealUsdPerMwh)) {
      resolvedVal = calc.postIncentiveRealUsdPerMwh;
      currencyBasis = 'real';
    } else if (isValidPerMwhUsd(calc.postIncentiveNominalUsdPerMwh)) {
      resolvedVal = calc.postIncentiveNominalUsdPerMwh;
      currencyBasis = 'nominal';
    } else if (isValidPerMwhUsd(usdCore.lcoeRealPerMwhUsd)) {
      resolvedVal = usdCore.lcoeRealPerMwhUsd;
      currencyBasis = 'real';
    } else if (isValidPerMwhUsd(usdCore.lcoeNominalPerMwhUsd)) {
      resolvedVal = usdCore.lcoeNominalPerMwhUsd;
      currencyBasis = 'nominal';
    }

    if (resolvedVal == null) continue;

    const maturity = inferMaturityScenario(usdCore, fa);
    const existing = bucketed.get(bucket);
    if (!existing) {
      const maturities = new Set<'FOAK' | 'NOAK'>();
      if (maturity === 'FOAK' || maturity === 'NOAK') maturities.add(maturity);
      bucketed.set(bucket, {
        values: [resolvedVal],
        canonicalVal: isCanonical ? resolvedVal : null,
        currencyBasis,
        subsidyStatus: calc.subsidyStatus,
        incentiveStatus: calc.incentiveStatus,
        ineligibilityReason: calc.ineligibilityReason,
        disaggregatedCredits: calc.disaggregatedCredits,
        subsidySummary: calc.subsidySummary,
        maturities,
      });
    } else {
      existing.values.push(resolvedVal);
      if (isCanonical && existing.canonicalVal == null) {
        existing.canonicalVal = resolvedVal;
      }
      if (existing.currencyBasis !== currencyBasis) {
        existing.currencyBasis = undefined;
      }
      if (existing.subsidyStatus !== calc.subsidyStatus) {
        existing.subsidyStatus = undefined;
        existing.incentiveStatus = undefined;
        existing.disaggregatedCredits = undefined;
      } else if (calc.subsidyStatus === 'subsidized' && existing.subsidySummary !== calc.subsidySummary) {
        existing.subsidyStatus = undefined;
        existing.incentiveStatus = undefined;
        existing.disaggregatedCredits = undefined;
      }
      if (maturity === 'FOAK' || maturity === 'NOAK') {
        existing.maturities.add(maturity);
      }
    }
  }

  const out: Partial<Record<LcoeTechBucket, CorrectedLcoeEntry>> = {};
  for (const [bucket, acc] of bucketed.entries()) {
    const low = Math.min(...acc.values);
    const high = Math.max(...acc.values);
    const mid = acc.canonicalVal ?? low;
    const maturityScenario: CorrectedLcoeEntry['maturityScenario'] =
      acc.maturities.size === 2
        ? 'FOAK-and-NOAK'
        : acc.maturities.has('FOAK')
          ? 'FOAK'
          : acc.maturities.has('NOAK')
            ? 'NOAK'
            : 'unspecified';
    out[bucket] = {
      midUsdPerMwh: mid,
      lowUsdPerMwh: low,
      highUsdPerMwh: high,
      scenarioCount: acc.values.length,
      source: 'persisted',
      basis: 'project-finance',
      currencyYear: (fa as unknown as { currencyYear?: number | string | null }).currencyYear ?? undefined,
      currencyBasis: acc.currencyBasis,
      subsidyStatus: acc.subsidyStatus,
      incentiveStatus: acc.incentiveStatus,
      ineligibilityReason: acc.ineligibilityReason,
      disaggregatedCredits: acc.disaggregatedCredits,
      subsidySummary: acc.subsidySummary,
      firmingBasis: 'plant-level-model',
      serviceScope: 'busbar',
      maturityScenario,
    };
  }
  return out;
}

/**
 * Resolve the corrected LCOE surface for a report — the SINGLE source both the
 * BTM overview table and the LCOE chart read.
 *
 * Precedence: persisted `pathwayLcoeAtGen` (Track B) / saved user financial
 * model (`financialAssumptionsAtGen`) → live compute from the gen-time intent
 * → pending. Pending buckets carry `null` so the renderers show "pending",
 * never a fabricated literature band.
 */
export function resolveReportCorrectedLcoe(
  report: SiteRagReport,
  opts?: { now?: Date },
): CorrectedLcoeSurface {
  const now = opts?.now ?? new Date();
  const modelEntries = readSavedFinancialModelEntries(report);
  const hasModelEntries = Object.keys(modelEntries).length > 0;

  const persisted = readPersistedSurface(report);
  if (persisted) {
    for (const [bucket, entry] of Object.entries(modelEntries) as Array<[LcoeTechBucket, CorrectedLcoeEntry]>) {
      if (persisted[bucket].midUsdPerMwh == null) {
        persisted[bucket] = entry;
      }
    }
    return {
      byBucket: persisted,
      source: 'persisted',
      hasAnyCorrected: true,
    };
  }

  const live = computeLiveSurface(report, now);
  if (live) {
    for (const [bucket, entry] of Object.entries(modelEntries) as Array<[LcoeTechBucket, CorrectedLcoeEntry]>) {
      live[bucket] = entry;
    }
    return {
      byBucket: live,
      source: 'computed',
      hasAnyCorrected: true,
    };
  }

  if (hasModelEntries) {
    const byBucket = emptyByBucket();
    for (const [bucket, entry] of Object.entries(modelEntries) as Array<[LcoeTechBucket, CorrectedLcoeEntry]>) {
      byBucket[bucket] = entry;
    }
    return {
      byBucket,
      source: 'persisted',
      hasAnyCorrected: true,
    };
  }

  return {
    byBucket: emptyByBucket(),
    source: 'pending',
    hasAnyCorrected: false,
  };
}

/**
 * Corrected LCOE for a single render bucket, or pending when unavailable.
 * Convenience for the chart rows / table cells which key by bucket.
 */
export function correctedLcoeForBucket(
  surface: CorrectedLcoeSurface,
  bucket: LcoeTechBucket | null,
): CorrectedLcoeEntry {
  if (!bucket) return pendingEntry();
  return surface.byBucket[bucket];
}

/**
 * Part C SP4 (S2) — the WORST (highest) corrected $/MWh across the surface's
 * resolved buckets. This is the figure the FINANCIAL PILLAR scores against:
 * the costliest path the site would actually build, drawn from the SAME
 * canonical corrected-LCOE surface the report renders (BTM table + chart), so
 * the pillar score and the prose/chart can never quote divergent $/MWh.
 *
 * Each `byBucket` entry already holds the LOWEST figure per technology bucket
 * (most-representative per tech). Taking the MAX across those bucket figures
 * yields "worst-of-the-min-per-bucket": the highest LCOE among the technologies
 * the site is comparing. Returns `null` when no bucket carries a real figure
 * (surface pending) so the caller can fall through to its legacy path.
 */
export function worstCorrectedLcoe(surface: CorrectedLcoeSurface): number | null {
  let worst: number | null = null;
  for (const bucket of Object.values(surface.byBucket)) {
    const v = bucket.midUsdPerMwh;
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
      if (worst == null || v > worst) worst = v;
    }
  }
  return worst;
}
